import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight'
import SubdirectoryArrowRightIcon from '@mui/icons-material/SubdirectoryArrowRight'
import { Box, IconButton, Link, TableCell, TableRow, Tooltip, Typography } from '@mui/material'
import { SxProps } from '@mui/system'
import { To } from 'history'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useHistory } from '@/hooks'
import { TenantChildDto, TenantHierarchyDto } from '@/services/api/tenant/subsidiaries'

import {
  chevronIconStyles,
  childIndentBoxStyles,
  companyCellStyles,
  companyNameBoxStyles,
  expandButtonStyles,
  expandedRowStyles,
  hierarchyIconStyles,
  linkStyles,
  secondaryTextStyles,
  standaloneNameWrapperStyles,
} from './TenantHierarchyTableRowStyles'

type Props = {
  item: TenantHierarchyDto
  searchQuery?: string
}

type ChildRowProps = {
  child: TenantChildDto
  parentName: string
  onNavigate: (subdomain: string, id: string) => void
  indented?: boolean
}

type NameLinkProps = {
  name: string
  onClick: () => void
  typographySx?: SxProps
}

const NameLink = ({ name, onClick, typographySx }: NameLinkProps) => {
  const linkRef = useRef<HTMLButtonElement>(null)
  const [isOverflowing, setIsOverflowing] = useState(false)

  useEffect(() => {
    if (linkRef.current) {
      setIsOverflowing(linkRef.current.scrollWidth > linkRef.current.offsetWidth)
    }
  }, [name])

  return (
    <Tooltip
      title={name}
      disableHoverListener={!isOverflowing}
      disableFocusListener={!isOverflowing}
      disableTouchListener={!isOverflowing}>
      <Typography color="textPrimary" variant="body1" sx={typographySx}>
        <Link ref={linkRef} component="button" sx={linkStyles} color="inherit" onClick={onClick}>
          {name}
        </Link>
      </Typography>
    </Tooltip>
  )
}

const ChildRow = ({ child, parentName, onNavigate, indented = true }: ChildRowProps) => {
  const { t } = useTranslation('general')
  return (
    <TableRow hover>
      <TableCell sx={companyCellStyles}>
        <Box sx={indented ? childIndentBoxStyles : standaloneNameWrapperStyles}>
          {indented && <SubdirectoryArrowRightIcon sx={hierarchyIconStyles} />}
          <NameLink
            name={child.name}
            onClick={() => onNavigate(child.subdomain, child.id)}
            typographySx={{ minWidth: 0 }}
          />
        </Box>
      </TableCell>
      <TableCell>
        <Typography variant="body1">{t('tenant_type_subsidiary')}</Typography>
        <Typography variant="body2" sx={secondaryTextStyles} noWrap>
          {t('tenant_type_parent')}: {parentName}
        </Typography>
      </TableCell>
    </TableRow>
  )
}

export const TenantHierarchyTableRow = ({ item, searchQuery }: Props) => {
  const { t } = useTranslation('general')
  const history = useHistory()
  const rootUrl = useMemo(() => new URL(document?.URL), [])

  const displayChildren = useMemo(() => {
    if (!searchQuery) return item.children
    const lower = searchQuery.toLowerCase()
    return item.children.filter((c) => c.name.toLowerCase().includes(lower))
  }, [item.children, searchQuery])

  const hasMatchingChildren = displayChildren.length > 0
  const [open, setOpen] = useState(!!searchQuery && hasMatchingChildren)

  useEffect(() => {
    setOpen(!!searchQuery && hasMatchingChildren)
  }, [searchQuery, hasMatchingChildren])

  const navigate = (subdomain: string, id: string) => {
    const currentSubdomain = rootUrl.hostname.replace('www.', '').split('.')[0]
    const newUrl = rootUrl.href.replace(`${currentSubdomain}.`, `${subdomain}.`) + `/${id}`
    history.push(newUrl as To, {})
  }

  if (item.type === 'child') {
    return <ChildRow child={item.tenant} parentName={item.parent?.name || ''} onNavigate={navigate} indented={false} />
  }

  if (item.type === 'standalone') {
    return (
      <TableRow hover>
        <TableCell sx={companyCellStyles}>
          <Box sx={standaloneNameWrapperStyles}>
            <NameLink name={item.tenant.name} onClick={() => navigate(item.tenant.subdomain, item.tenant.id)} />
          </Box>
        </TableCell>
        <TableCell>
          <Typography variant="body1">{t('tenant_type_standalone')}</Typography>
        </TableCell>
      </TableRow>
    )
  }

  // type === 'parent'
  const subsidiaryCount = item.children.length
  const childrenToRender = hasMatchingChildren ? displayChildren : item.children
  return (
    <React.Fragment>
      <TableRow hover sx={open ? expandedRowStyles : undefined}>
        <TableCell sx={companyCellStyles}>
          <Box sx={companyNameBoxStyles}>
            <IconButton
              size="small"
              disableRipple
              onClick={() => setOpen(!open)}
              sx={expandButtonStyles}
              aria-label={open ? t('tenant_table_collapse_subsidiaries') : t('tenant_table_expand_subsidiaries')}
              aria-expanded={open}>
              {open ? (
                <KeyboardArrowDownIcon sx={chevronIconStyles} />
              ) : (
                <KeyboardArrowRightIcon sx={chevronIconStyles} />
              )}
            </IconButton>
            <NameLink
              name={item.tenant.name}
              onClick={() => navigate(item.tenant.subdomain, item.tenant.id)}
              typographySx={{ minWidth: 0 }}
            />
          </Box>
        </TableCell>
        <TableCell>
          <Typography variant="body1">{t('tenant_type_parent')}</Typography>
          <Typography variant="body2" sx={secondaryTextStyles}>
            {subsidiaryCount} {subsidiaryCount === 1 ? t('tenant_type_subsidiary') : t('tenant_table_subsidiaries')}
          </Typography>
        </TableCell>
      </TableRow>

      {open &&
        childrenToRender.map((child) => (
          <ChildRow key={child.id} child={child} parentName={item.tenant.name} onNavigate={navigate} />
        ))}
    </React.Fragment>
  )
}
